import React, { useEffect, useRef, useState } from 'react';
import './ComposicionSpcPremium.css';

/*
  Composición del SPC — vista explosionada interactiva, 100% código (SVG + CSS).
  - Sin imágenes ni librerías. Estilos propios en ComposicionSpcPremium.css.
  - Las capas se separan al entrar en pantalla (IntersectionObserver, una sola vez).
  - Hover, foco o toque sobre una capa (o su fila) la resalta: número y contorno de la card en azul.
  - La lista es la versión accesible/legible: funciona con teclado y lector de pantalla.
  - Respeta prefers-reduced-motion (se muestra abierta y sin animación).
  Contiene el contenido del antiguo bloque de reverso: los beneficios de la base acolchada van
  dentro de "Manta acústica" y las fotos del revés se muestran como miniaturas.
*/

/* ── Datos ─────────────────────────────────────────────────────────────── */

// Textos tomados de la imagen original del cliente (de arriba hacia abajo)
const CAPAS = [
  { id: 'pu', capa: 'Película PU', detalle: 'protege de los rayos UV', grosor: 4 },
  { id: 'uso', capa: 'Capa de uso', detalle: 'determina el tráfico del piso', grosor: 8 },
  { id: 'decorativa', capa: 'Lámina decorativa', detalle: 'impresión digital con diseño', grosor: 5 },
  { id: 'spc', capa: 'Cuerpo SPC', detalle: 'piedra caliza + PVC', nota: '(STONE PVC)', grosor: 22 },
  {
    id: 'manta',
    capa: 'Manta acústica',
    detalle: 'reduce ruido de impacto y brinda mayor confort al caminar',
    grosor: 10,
    extras: [
      { icono: 'fa-shoe-prints', titulo: 'Base Acolchada Integrada', texto: 'Proporciona comodidad al caminar con una superficie suave y absorbente bajo cada paso.' },
      { icono: 'fa-temperature-half', titulo: 'Barrera Térmica', texto: 'Mejora el aislamiento térmico del piso, ideal para superficies frías o climas extremos.' },
      { icono: 'fa-volume-xmark', titulo: 'Reducción de Ruido', texto: 'Aislamiento acústico superior que absorbe el impacto de los pasos y reduce la transmisión de sonido.' },
    ],
  },
];

const REVERSO = [1, 2, 3, 4].map((n) => ({
  src: `/assets/img/productos/pisos-spc-premium/carrusel_reves/carrusel_${n}.jpeg`,
  alt: `Reverso Piso SPC Premium - Vista ${n}`,
}));

const AYUDA = 'Pasa el cursor o toca una capa para resaltarla';

// Colores de cada capa (caras superior/frontal/lateral, trazo y textura opcional)
const ESTILOS_CAPA = {
  pu: { fill: 'url(#spcg-pu)', frente: '#b8d3f2', lado: '#a3c3ea', trazo: '#8fb6e6' },
  uso: { fill: 'url(#spcg-uso)', frente: '#ecc98c', lado: '#dfb773', trazo: '#d9ae62' },
  decorativa: { fill: 'url(#spcg-deco)', frente: '#8f5d2c', lado: '#7a4d22', trazo: '#7a4d22', textura: 'url(#spcp-grano)' },
  spc: { fill: 'url(#spcg-spc)', frente: '#737d86', lado: '#626b73', trazo: '#626b73', textura: 'url(#spcp-piedra)' },
  manta: { fill: 'url(#spcg-manta)', frente: '#b5babf', lado: '#a2a7ad', trazo: '#a2a7ad', textura: 'url(#spcp-espuma)' },
};

/* ── Geometría isométrica (unidades del viewBox) ───────────────────────── */

const X0 = 40;                 // esquina frontal izquierda
const U = { x: 330, y: 66 };   // largo del panel
const V = { x: 120, y: -66 };  // ancho del panel (hacia el fondo)
const SEPARACION = 62;         // distancia entre capas abiertas
const Y_INICIO = 110;          // y de la capa superior abierta

const pts = (...p) => p.map(([x, y]) => `${x},${y}`).join(' ');

// Posición abierta/compacta de cada capa (se calcula una sola vez, fuera del render)
const Y_FONDO_COMPACTO = Y_INICIO + SEPARACION * (CAPAS.length - 1);
const GEOMETRIA = CAPAS.map((capa, i) => {
  const yAbierta = Y_INICIO + SEPARACION * i;
  const yCompacta = CAPAS.slice(i, -1).reduce((y, c) => y - c.grosor, Y_FONDO_COMPACTO);
  const O = [X0, yAbierta];
  const A = [X0 + U.x, yAbierta + U.y];
  const B = [A[0] + V.x, A[1] + V.y];
  const C = [X0 + V.x, yAbierta + V.y];
  const t = capa.grosor;
  return {
    top: pts(O, A, B, C),
    front: pts(O, A, [A[0], A[1] + t], [O[0], O[1] + t]),
    side: pts(A, B, [B[0], B[1] + t], [A[0], A[1] + t]),
    badge: [B[0] + 22, B[1] + t / 2],
    shift: yCompacta - yAbierta,
  };
});

// La capa superior se dibuja al final
const ORDEN_DIBUJO = CAPAS.map((_, i) => i).reverse();

/* ── Subcomponentes ────────────────────────────────────────────────────── */

const Degradado = ({ id, de, a, opacidad }) => (
  <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
    <stop offset="0" stopColor={de} stopOpacity={opacidad?.[0]} />
    <stop offset="1" stopColor={a} stopOpacity={opacidad?.[1]} />
  </linearGradient>
);

const DefsSvg = () => (
  <defs>
    <Degradado id="spcg-pu" de="#f2f8ff" a="#cfe3fb" opacidad={[0.9, 0.75]} />
    <Degradado id="spcg-uso" de="#fff3d8" a="#f3d9a4" opacidad={[0.92, 0.8]} />
    <Degradado id="spcg-deco" de="#d9a76c" a="#b8814a" />
    <Degradado id="spcg-spc" de="#a9b2ba" a="#8b959e" />
    <Degradado id="spcg-manta" de="#e3e6e9" a="#cdd1d5" />

    {/* Texturas alineadas con el largo del panel */}
    <pattern id="spcp-grano" width="46" height="14" patternUnits="userSpaceOnUse" patternTransform="rotate(11.3)">
      <path d="M0 4 C12 1 24 7 46 4" fill="none" stroke="#7a4a1e" strokeOpacity=".35" strokeWidth="1" />
      <path d="M0 10 C14 13 30 8 46 11" fill="none" stroke="#7a4a1e" strokeOpacity=".25" strokeWidth="1" />
    </pattern>
    <pattern id="spcp-piedra" width="14" height="14" patternUnits="userSpaceOnUse">
      <circle cx="3" cy="4" r="1.1" fill="#fff" fillOpacity=".35" />
      <circle cx="10" cy="9" r="1.3" fill="#4d565e" fillOpacity=".3" />
      <circle cx="6" cy="12" r=".8" fill="#fff" fillOpacity=".3" />
    </pattern>
    <pattern id="spcp-espuma" width="9" height="9" patternUnits="userSpaceOnUse">
      <circle cx="2.5" cy="2.5" r="1.4" fill="#9aa0a6" fillOpacity=".45" />
      <circle cx="7" cy="7" r="1.1" fill="#9aa0a6" fillOpacity=".35" />
    </pattern>
  </defs>
);

/* ── Componente ────────────────────────────────────────────────────────── */

const ComposicionSpcPremium = () => {
  const stageRef = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [listo, setListo] = useState(false); // terminó la animación de apertura
  const [hover, setHover] = useState(null);
  const [fija, setFija] = useState(null);
  const activa = hover ?? fija;

  // Apertura al entrar en pantalla (una sola vez)
  useEffect(() => {
    const reducir = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reducir || !('IntersectionObserver' in window)) {
      setAbierto(true);
      setListo(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAbierto(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    if (stageRef.current) obs.observe(stageRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!abierto || listo) return undefined;
    const t = setTimeout(() => setListo(true), 1200);
    return () => clearTimeout(t);
  }, [abierto, listo]);

  // Hover solo con mouse; en táctil se usa el toque (fija)
  const entrar = (i) => (e) => {
    if (e.pointerType === 'mouse') setHover(i);
  };
  const salir = (e) => {
    if (e.pointerType === 'mouse') setHover(null);
  };
  const alternar = (i) => () => setFija((prev) => (prev === i ? null : i));

  const clases = ['spc-comp', abierto && 'is-open', listo && 'is-settled', activa !== null && 'has-active']
    .filter(Boolean)
    .join(' ');

  return (
    <section id="composicion" className="spc-comp-section spc-section spc-section--before-dark">
      <div className="container">
        <div className="spc-comp__head wow fadeIn" data-wow-delay="200ms">
          <div className="title-style5">
            <h2 className="display-5 lh-1 font-weight-800 mb-0">Composición del Piso SPC Luxury</h2>
          </div>
        </div>

        <div className={clases}>
          <div className="spc-comp__grid">
            {/* Diagrama + revés del piso */}
            <div className="spc-comp__stage" ref={stageRef}>
              <svg className="spc-comp__svg" viewBox="0 30 545 420" aria-hidden="true" focusable="false">
                <DefsSvg />
                {ORDEN_DIBUJO.map((i) => {
                  const g = GEOMETRIA[i];
                  const { id } = CAPAS[i];
                  const e = ESTILOS_CAPA[id];
                  return (
                    <g
                      key={id}
                      className={`spc-layer${activa === i ? ' is-active' : ''}`}
                      style={{ '--shift': `${g.shift}px`, '--delay': `${i * 90}ms` }}
                      onPointerEnter={entrar(i)}
                      onPointerLeave={salir}
                      onClick={alternar(i)}
                    >
                      <polygon points={g.front} fill={e.frente} stroke={e.trazo} strokeWidth="1" strokeLinejoin="round" />
                      <polygon points={g.side} fill={e.lado} stroke={e.trazo} strokeWidth="1" strokeLinejoin="round" />
                      <polygon className="spc-layer__top" points={g.top} fill={e.fill} stroke={e.trazo} strokeWidth="1.2" strokeLinejoin="round" />
                      {e.textura && <polygon points={g.top} fill={e.textura} pointerEvents="none" />}
                      <g className="spc-layer__badge" transform={`translate(${g.badge[0]} ${g.badge[1]})`}>
                        <circle r="13" />
                        <text textAnchor="middle" dy=".36em">{i + 1}</text>
                      </g>
                    </g>
                  );
                })}
              </svg>
              <p className="spc-comp__hint">{AYUDA}</p>

              <div className="spc-comp__reverso">
                <h3 className="spc-comp__reverso-title">Revés del Piso SPC Luxury</h3>
                <ul className="spc-comp__thumbs">
                  {REVERSO.map(({ src, alt }) => (
                    <li key={src}>
                      <img src={src} alt={alt} loading="lazy" />
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Capas: texto legible, accesible y táctil */}
            <ol className="spc-comp__list">
              {CAPAS.map(({ id, capa, detalle, nota, extras }, i) => (
                <li
                  key={id}
                  className={`spc-comp__row${activa === i ? ' is-active' : ''}`}
                  onPointerEnter={entrar(i)}
                  onPointerLeave={salir}
                >
                  <button
                    type="button"
                    className="spc-comp__item"
                    aria-pressed={fija === i}
                    onFocus={() => setHover(i)}
                    onBlur={() => setHover(null)}
                    onClick={alternar(i)}
                  >
                    <span className="spc-comp__num" aria-hidden="true">{i + 1}</span>
                    <span className="spc-comp__txt">
                      <strong>{capa}:</strong> {detalle}
                      {nota && <em> {nota}</em>}
                    </span>
                  </button>

                  {extras && (
                    <ul className="spc-comp__extras">
                      {extras.map((extra) => (
                        <li className="spc-comp__extra" key={extra.titulo}>
                          <i className={`fa-solid ${extra.icono}`} aria-hidden="true"></i>
                          <div>
                            <h4>{extra.titulo}</h4>
                            <p>{extra.texto}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComposicionSpcPremium;